import { useCallback, useEffect } from 'react';
import { useClient } from 'shared/apollo';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { isReportTaskDismissed } from './dismissedReportTasks';
import { getUserPrograms } from '../ProgramsAndReports/api';
import {
  GetUserProgramsResponse,
  UserProgram,
} from '../ProgramsAndReports/types';

const PAGE_SIZE = 1000;

const programHasCurrentYearReport = (p: UserProgram, currentYear: number) =>
  !!p.reports?.some(r => {
    if (r.year == null) return false;
    const y =
      typeof r.year === 'number' ? r.year : parseInt(String(r.year), 10);
    return y === currentYear;
  });

export const hasActionableThirdPartyTasks = (
  programs: UserProgram[],
  currentYear: number = new Date().getFullYear(),
): boolean => {
  if (programs.length === 0) return true;
  const hasIncompleteProfile = programs.some(p => !p.program.isCompleted);
  if (hasIncompleteProfile) return true;
  return programs.some(
    p =>
      p.program.isCompleted &&
      !programHasCurrentYearReport(p, currentYear) &&
      !isReportTaskDismissed(p.program.dbId, currentYear),
  );
};

export interface ThirdPartyTodoActionableState {
  loaded: boolean;
  actionable: boolean;
  hasError: boolean;
  programs: UserProgram[];
  reload: () => void;
}

export const useThirdPartyTodoActionable = (
  enabled: boolean,
): ThirdPartyTodoActionableState => {
  const client = useClient();
  const [request, makeRequest] = useGraphQLRequest<GetUserProgramsResponse>(
    undefined,
    true,
  );
  const reload = useCallback(() => {
    if (!enabled || !client) return;
    makeRequest(getUserPrograms(client, { first: PAGE_SIZE, offset: 0 }));
  }, [enabled, client, makeRequest]);

  useEffect(() => {
    reload();
  }, [reload]);

  if (!enabled) {
    return {
      loaded: true,
      actionable: false,
      hasError: false,
      programs: [],
      reload,
    };
  }
  if (request.type !== 'REQUEST_SUCCESS' && request.type !== 'REQUEST_ERROR') {
    return {
      loaded: false,
      actionable: false,
      hasError: false,
      programs: [],
      reload,
    };
  }
  if (request.type === 'REQUEST_ERROR') {
    return {
      loaded: true,
      actionable: true,
      hasError: true,
      programs: [],
      reload,
    };
  }
  const payload = request.data.getUserPrograms;
  const programs = payload?.success ? payload.programs : [];
  return {
    loaded: true,
    actionable: hasActionableThirdPartyTasks(programs),
    hasError: false,
    programs,
    reload,
  };
};
